import { useCallback, useState, type ReactNode } from 'react';

interface SlidePanelProps {
  open: boolean;
  storageKey: string;
  children: ReactNode;
  /** Pixel floor for drag. Default 350. */
  minWidth?: number;
  /** Fraction of viewport used as max width. Default 0.5. */
  maxPct?: number;
  /** Pixel fallback when window is unavailable. Default 480. */
  fallbackWidth?: number;
}

/**
 * Right-docked draggable slide panel used by Rocks, KPI, Headlines, OS To-Dos,
 * Meetings, VTO, etc. Owns drag state, width persistence, and the fixed
 * top-[64px] layout so individual pages don't duplicate the logic.
 *
 * Default opens at 1/3 of the viewport and can be dragged out to 50vw (the
 * middle of the screen). Width is saved per-page via `storageKey`.
 */
export function SlidePanel({
  open,
  storageKey,
  children,
  minWidth = 350,
  maxPct = 0.5,
  fallbackWidth = 480,
}: SlidePanelProps) {
  const [panelWidth, setPanelWidth] = useState(() => {
    if (typeof window === 'undefined') return fallbackWidth;
    const saved = window.localStorage.getItem(storageKey);
    if (saved) {
      const n = parseInt(saved, 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    return Math.round(window.innerWidth / 3);
  });

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = panelWidth;
    let dragging = true;

    const onMove = (ev: MouseEvent) => {
      if (!dragging) return;
      const delta = startX - ev.clientX;
      const viewport = window.innerWidth;
      const newWidth = Math.min(
        Math.max(startWidth + delta, minWidth),
        viewport * maxPct,
      );
      setPanelWidth(newWidth);
    };

    const onUp = () => {
      dragging = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth((w) => {
        window.localStorage.setItem(storageKey, String(w));
        return w;
      });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [panelWidth, minWidth, maxPct, storageKey]);

  if (!open) return null;

  return (
    <div
      className="fixed top-[64px] right-0 bottom-0 flex z-50"
      style={{ width: panelWidth, maxWidth: '50vw' }}
    >
      <div
        className="w-1.5 shrink-0 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors bg-border"
        onMouseDown={handleDragStart}
      />
      <div className="flex-1 flex flex-col border-l bg-background shadow-lg overflow-hidden [&_input]:bg-white [&_textarea]:bg-white [&_[role=combobox]]:bg-white">
        {children}
      </div>
    </div>
  );
}
